import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  LayoutDashboard,
  ShoppingBag,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  X,
  User,
  HelpCircle,
  Activity,
} from "lucide-react";
import { useComingSoon } from "../../../utils/useComingSoon";

export default function UserSidebar({ onClose, collapsed, onToggleCollapse }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeItem, setActiveItem] = useState("Overview");
  const { showComingSoon } = useComingSoon();

  const navigationItems = useMemo(() => [
    { name: "Overview", icon: LayoutDashboard, path: "/dashboard" },
    { name: "My Rentals", icon: ShoppingBag, path: "/dashboard/rentals" },
    { name: "Recent Activity", icon: Activity, path: "/dashboard/activity" },
    { name: "Settings", icon: Settings, path: "/dashboard/settings" },
  ], []);

  useEffect(() => {
    const active = navigationItems.find(item => {
      if (item.name === "Overview") {
        return location.pathname === "/dashboard" || location.pathname === "/dashboard/";
      }
      return location.pathname.startsWith(item.path);
    });
    if (active) setActiveItem(active.name);
  }, [location.pathname, navigationItems]);

  const handleLogout = () => {
    // Mock logout
    navigate("/login");
  };

  return (
    <div className={`${collapsed ? "w-20" : "w-64"} bg-white dark:bg-[#1A1A1A] flex-shrink-0 flex flex-col h-full border-r border-[#E5E7EB] dark:border-[#2A2A2A] transition-all duration-300 relative z-50`}>
      {/* Brand Logo */}
      <div className="p-6 flex items-center justify-between border-b border-[#E5E7EB] dark:border-[#2A2A2A]">
        {!collapsed && (
          <div className="flex items-center gap-3">
            <div onClick={() => navigate("/")} className="w-10 h-10 bg-gradient-to-br from-[#62397f] to-[#7C3AED] flex items-center justify-center shadow-lg cursor-pointer">
              <img src="/static/images/logo.webp" alt="Logo" className="w-10 h-10 object-contain" />
            </div>
            <div>
              <h2 className="font-medium text-[#111111] dark:text-white text-sm font-serif">St. Stephen's</h2>
              <p className="text-xs text-[#666666] dark:text-[#A0A0A0] font-inter">User Account</p>
            </div>
            <button onClick={onClose} className="lg:hidden p-2 text-[#666666] dark:text-[#A0A0A0] hover:text-[#111111] dark:hover:text-white ml-auto">
              <X size={20} />
            </button>
          </div>
        )}
        {collapsed && (
          <div className="w-10 h-10 flex items-center justify-center mx-auto cursor-pointer" onClick={() => navigate("/")}>
            <img src="/static/images/logo.webp" alt="Logo" className="w-10 h-10 object-contain" />
          </div>
        )}
      </div>

      {/* Navigation Menu */}
      <nav className="flex-1 px-3 py-4 overflow-y-auto">
        <div className="space-y-1">
          {navigationItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeItem === item.name;

            return (
              <Link
                key={item.name}
                to={item.path}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${isActive
                  ? "bg-gradient-to-r from-[#F5F3FF] to-[#FAF5FF] dark:from-[#2A2440] dark:to-[#2A2440] border border-[#62397f] dark:border-[#7C3AED] text-[#62397f] dark:text-[#A78BFA]"
                  : "text-[#666666] dark:text-[#A0A0A0] hover:bg-[#F9FAFB] dark:hover:bg-[#262626] hover:text-[#111111] dark:hover:text-white"
                  }`}
                title={collapsed ? item.name : ""}
              >
                <Icon size={20} strokeWidth={isActive ? 2.5 : 2} />
                {!collapsed && <span className={`font-medium text-sm font-inter ${isActive ? "font-semibold" : ""}`}>{item.name}</span>}
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Support Button */}
      <div className="px-3">
        <button
          onClick={() => {
            if (onClose) onClose();
            showComingSoon({
              title: "Support Coming Soon!",
              message: "We're building the support feature. Stay tuned!"
            });
          }}
          className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 text-[#666666] dark:text-[#A0A0A0] hover:bg-[#F9FAFB] dark:hover:bg-[#262626] hover:text-[#111111] dark:hover:text-white`}
          title={collapsed ? "Support" : ""}
        >
          <HelpCircle size={20} strokeWidth={2} />
          {!collapsed && <span className="font-medium text-sm font-inter">Support</span>}
        </button>
      </div>

      {/* Logout & Collapse */}
      <div className="p-3 border-t border-[#E5E7EB] dark:border-[#2A2A2A] space-y-1">
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10 active:scale-[0.98]"
        >
          <LogOut size={20} />
          {!collapsed && <span className="font-medium text-sm font-inter">Logout</span>}
        </button>

        <button
          onClick={onToggleCollapse}
          className="hidden lg:flex w-full items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-[#666666] dark:text-[#A0A0A0] hover:bg-[#F9FAFB] dark:hover:bg-[#262626] transition-all"
        >
          {collapsed ? <ChevronRight size={18} /> : <><ChevronLeft size={18} /><span className="text-sm font-medium">Collapse</span></>}
        </button>
      </div>
    </div>
  );
}
